import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { Observable } from 'rxjs';

export interface BreadcrumbItem {
  label: string;
  link?: string;
}

function normalizeUrlPath(url: string): string {
  const path = url.split('?')[0].split('#')[0].replace(/\/+$/, '');
  return path || '/';
}

function hasNotificationType(path: string, type: string): boolean {
  return new RegExp(`^/notification/(?:[^/]+/)?${type}(?:/|$)`).test(path);
}

function getBreadcrumbsForUrl(url: string): BreadcrumbItem[] {
  const path = normalizeUrlPath(url);
  const labels: string[] = [];

  if (/^\/balance(?:\/|$)/.test(path)) labels.push('清算情報');
  if (/^\/expelled(?:\/|$)/.test(path)) labels.push('除名者');
  if (/^\/inout(?:\/|$)/.test(path)) labels.push('入退館履歴');
  if (/^\/invoice(?:\/|$)/.test(path)) labels.push('請求情報');
  if (/^\/member(?:\/|$)/.test(path)) labels.push('会員情報');
  if (/^\/insurance(?:\/|$)/.test(path)) labels.push('会員情報', '保険適応');
  if (/^\/counsel(?:\/|$)/.test(path)) labels.push('カウンセリング');
  if (/^\/owner(?:\/|$)/.test(path)) labels.push('企業');
  if (/^\/product(?:\/|$)/.test(path)) labels.push('商品');
  if (/^\/staff(?:\/|$)/.test(path)) labels.push('従業員');
  if (/^\/studio(?:\/|$)/.test(path)) labels.push('店舗');
  if (/^\/tax[-_]rate(?:\/|$)/.test(path)) labels.push('消費税');
  if (/^\/user(?:\/|$)/.test(path)) labels.push('ユーザー');
  if (/^\/notification(?:\/|$)/.test(path)) labels.push('諸届申請');
  if (/^\/subscription(?:\/|$)/.test(path)) labels.push('物販定期便');
  if (/^\/password[-_]reset(?:\/|$)/.test(path)) labels.push('パスワード付与');
  if (/^\/marketing(?:\/|$)/.test(path)) labels.push('マーケティング');
  if (/^\/email(?:\/|$)/.test(path)) labels.push('メール配信');
  if (/^\/inout[-_]management(?:\/|$)/.test(path)) labels.push('KEYVOX連携');

  if (/^\/notification\/.+/.test(path)) labels.push('会員別諸届申請');
  if (/^\/subscription\/[0-9]+$/.test(path)) labels.push('会員別物販定期便');
  if (hasNotificationType(path, 'locker')) labels.push('ロッカー');
  if (hasNotificationType(path, 'payment')) labels.push('支払方法');
  if (hasNotificationType(path, 'plan')) labels.push('区分コース');
  if (hasNotificationType(path, 'subscription')) labels.push('物販定期便');
  if (hasNotificationType(path, 'studio')) labels.push('移籍');
  if (hasNotificationType(path, 'suspend')) labels.push('休会･退会');
  if (hasNotificationType(path, 'expel')) labels.push('除名');

  if (/^\/studio\/(?:[^/]+\/)?edit(?:\/|$)/.test(path)) labels.push('基本情報');
  if (/^\/studio\/(?:[^/]+\/)?fee(?:\/|$)/.test(path)) labels.push('清算用情報');
  if (/^\/studio\/(?:[^/]+\/)?key(?:\/|$)/.test(path)) labels.push('入退館キー');
  if (/^\/studio\/(?:[^/]+\/)?locker(?:\/|$)/.test(path)) labels.push('ロッカー');
  if (/^\/studio\/(?:[^/]+\/)?plan/.test(path)) labels.push('区分コース');
  if (/^\/studio\/(?:plan_edit|[^/]+\/plan\/[^/]+\/edit)(?:\/|$)/.test(path)) labels.push('編集');

  if (/^\/transaction(?:\/|$)/.test(path)) labels.push('売上管理');
  if (/^\/transaction\/.*\/all(?:\/|$)/.test(path)) labels.push('会員別売上');

  if (/^\/aggregation\/member[-_]ratio(?:\/|$)/.test(path)) labels.push('会員比率');
  if (/^\/aggregation\/membership(?:\/|$)/.test(path)) labels.push('会員推移');
  if (/^\/aggregation\/questionary(?:\/|$)/.test(path)) labels.push('アンケート');

  if (/^\/marketing\/all(?:\/|$)/.test(path)) labels.push('全店');
  if (/^\/marketing\/studio(?:\/|$)/.test(path)) labels.push('個店');
  if (/^\/marketing\/promotion(?:\/|$)/.test(path)) labels.push('販促実施状況');
  if (/^\/marketing\/flyer(?:\/|$)/.test(path)) labels.push('入会獲得数最多店舗');

  if (/^\/counsel\/.*\/personal(?:\/|$)/.test(path)) labels.push('パーソナルデータ');

  if (/^\/email\/content(?:\/|$)/.test(path)) labels.push('文面作成');
  if (/^\/email\/reservation(?:\/|$)/.test(path)) labels.push('配信予約');
  if (/^\/email\/settings(?:\/|$)/.test(path)) labels.push('設定');

  if (/^\/password[-_]reset\/execute(?:\/|$)/.test(path)) labels.push('実行');

  if (!/^\/studio(?:\/|$)/.test(path) && (/(?:^|\/)edit(?:\/|$)/.test(path) || /^\/product\/.*\/product(?:\/|$)/.test(path))) {
    labels.push('編集');
  }
  if (/(?:^|\/)(?:new|plan_new)(?:\/|$)/.test(path)) labels.push('新規作成');

  return labels.map(label => ({ label }));
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private router = inject(Router);

  readonly breadcrumbs$: Observable<BreadcrumbItem[]> = this.router.events.pipe(
    filter(e => e instanceof NavigationEnd),
    startWith(null),
    map(() => {
      const url = this.router.url.split('?')[0];
      return getBreadcrumbsForUrl(url);
    }),
  );
}
